// Колоните на „По категории".
//
// Таблицата е в КЛИЕНТСКИ режим: целият период идва наведнъж и се групира в
// браузъра, затова сортирането, филтърът и редът „Общо" се смятат тук.
//
// Кликът по реда е РАЗДЕЛЕН на две: името на категорията е линк към „По
// артикули" (`onOpenItems`), а останалата част от реда отваря разбивката по
// дни. Затова първата колона има СОБСТВЕН рендер вместо `meta.cell` —
// `meta.cell` бие `columnDef.cell` в DataTable.
//
// „Дял" и „Δ пред. период" не се сортират: делът повтаря подредбата по
// „Оборот", а Δ-то е чип — стрелка, която не мести нищо (или мести по текст),
// е по-лоша от липсваща.

import { useMemo } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";

import { formatPercent } from "../../../../components/dataTable";
import type {
  BarCellValue,
  ChipCellValue,
} from "../../../../components/dataTable";
import { CategoryNameCell } from "./CategoryNameCell";
import type { CategoryRow } from "./categoriesMath";

/** Минус знак U+2212 — по-широк от дефиса и подравнен с цифрите. */
const MINUS = "−";

const columnHelper = createColumnHelper<CategoryRow>();

type Options = {
  /** Показва ли се колоната „Δ пред. период". */
  showDelta: boolean;
  /** Името на категорията отваря „По артикули" за нея. */
  onOpenItems: (row: CategoryRow) => void;
};

export const useCategoriesColumns = ({ showDelta, onOpenItems }: Options) => {
  const { t } = useTranslation();

  return useMemo(
    () => [
      columnHelper.accessor((row) => row.categoryName, {
        id: "category",
        header: t("reportsPage.categoriesGroupColumn"),
        size: 220,
        cell: (context) => (
          <CategoryNameCell
            row={context.row.original}
            onOpenItems={onOpenItems}
          />
        ),
        // Клетката има собствен рендер, затова компараторът се пише явно:
        // без `meta.cell` таблицата не закача своя „по показания текст", а
        // вграденият на TanStack реди кирилицата по код на символа.
        sortingFn: (rowA, rowB, columnId) =>
          String(rowA.getValue(columnId) ?? "").localeCompare(
            String(rowB.getValue(columnId) ?? ""),
            undefined,
            { numeric: true, sensitivity: "base" },
          ),
        meta: {
          filter: { kind: "text" },
          alwaysVisible: true,
          excel: { format: "text" },
        },
      }),

      // БЕЗ `decimals` — теглените артикули идват с дробно количество.
      columnHelper.accessor((row) => row.quantity, {
        id: "quantity",
        header: t("reportsPage.itemsColCount"),
        size: 110,
        meta: {
          cell: { kind: "num" },
          total: "sum",
          excel: { format: "number" },
        },
      }),

      // Без `total`: сборът от средни цени не значи нищо.
      columnHelper.accessor((row) => row.avgPrice, {
        id: "avg_price",
        header: t("reportsPage.averagePrice"),
        size: 130,
        meta: {
          cell: { kind: "money" },
          hideBelow: "tablet",
          excel: { format: "money" },
        },
      }),

      columnHelper.accessor((row) => row.revenue, {
        id: "revenue",
        header: t("reportsPage.itemsColRevenue"),
        size: 132,
        meta: {
          cell: { kind: "money", strong: true },
          total: "sum",
          alwaysVisible: true,
          excel: { format: "money" },
        },
      }),

      columnHelper.accessor(
        (row): BarCellValue => ({
          value: row.sharePercent,
          max: 100,
          label: formatPercent(row.sharePercent, 1),
        }),
        {
          id: "share",
          header: t("reportsPage.itemsColShare"),
          size: 130,
          enableSorting: false,
          meta: { cell: { kind: "bar" }, hideBelow: "narrow" },
        },
      ),

      ...(showDelta
        ? [
            columnHelper.accessor(
              (row): ChipCellValue | null => {
                if (row.deltaPercent === null) return null;
                const isUp = row.deltaPercent >= 0;
                return {
                  tone: isUp ? "ok" : "danger",
                  label: `${isUp ? "+" : MINUS}${Math.abs(
                    row.deltaPercent,
                  ).toFixed(1)} %`,
                };
              },
              {
                id: "delta",
                header: t("reportsPage.categoriesColDelta"),
                size: 132,
                enableSorting: false,
                meta: { cell: { kind: "chip" }, hideBelow: "tablet" },
              },
            ),
          ]
        : []),
    ],
    [t, showDelta, onOpenItems],
  );
};
